import { API_METHODS } from '@compass/client/constants/api-methods';
import { useStore } from '@store/index';

import { QC_DISABLED_ORG_IDS } from '@/constants/feature-flags';
import { useIs100msUser } from '@/hooks/useIs100msUser';
import { usePermissionMap } from '@/hooks/usePermissions';

/**
 * Hook to check if the QC page should be shown and directly accessible.
 * This is a UI affordance only: evaluation/scorecard APIs still enforce
 * org/program permissions server-side via the backend auth annotations.
 *
 * Access rules:
 *   1. 100ms users (100ms.ai / 100ms.live email): always allowed across all orgs
 *   2. Non-100ms users: allowed unless their org is in REACT_APP_QC_DISABLED_ORG_IDS
 *   3. User must have permission to list evaluations or list scorecards
 */
export const useShouldShowQcPage = (contextOverride?: {
  organizationId?: string | null;
  programId?: string | null;
}): { shouldShow: boolean; isLoading: boolean } => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const effectiveOrganizationId =
    contextOverride && 'organizationId' in contextOverride
      ? contextOverride.organizationId
      : selectedOrganizationId;
  const is100msUser = useIs100msUser();
  const { data, isLoading } = usePermissionMap(contextOverride);

  const isOrgDisabled =
    !!effectiveOrganizationId && QC_DISABLED_ORG_IDS.has(effectiveOrganizationId);
  const isQcAllowed = is100msUser || !isOrgDisabled;

  const permissions = data?.permissions;
  const hasPermission =
    !!permissions &&
    (permissions[API_METHODS.EvaluationService.ListEvaluations] === true ||
      permissions[API_METHODS.ScorecardService.ListScorecards] === true);

  return { shouldShow: isQcAllowed && hasPermission, isLoading };
};
